import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ClickAwayListener, Paper, Popper, InputBase, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onAddNew?: () => void;
  placeholder?: string;
  addNewLabel?: string;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
}

export const SearchableSelect = ({
  options,
  value,
  onChange,
  onAddNew,
  placeholder = 'Select...',
  addNewLabel = 'Add New',
  onSearchChange,
  onLoadMore,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [internalValue, setInternalValue] = useState(value);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Client-side filtering only when onSearchChange is NOT provided (no server-side search)
  const filteredOptions = useMemo(() => {
    if (onSearchChange) return options; // server handles filtering
    if (!search.trim()) return options;
    const query = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query));
  }, [options, search, onSearchChange]);

  const selectedLabel = useMemo(() => {
    const found = options.find((opt) => opt.value === internalValue);
    return found ? found.label : internalValue;
  }, [options, internalValue]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      onSearchChange?.('');
      if (anchorRef.current && document.activeElement !== anchorRef.current) {
        anchorRef.current.focus();
      }
    }
  }, [open]);

  // Debounced server-side search
  useEffect(() => {
    if (!onSearchChange) return;
    const timer = setTimeout(() => {
      onSearchChange(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!open || !onLoadMore || !hasNextPage) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { root: listRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, onLoadMore, hasNextPage, isFetchingNextPage]);

  const handleSelect = (val: string) => {
    setInternalValue(val);
    onChange?.(val);
    setOpen(false);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  return (
    <div className='relative w-full'>
      <button
        ref={anchorRef}
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-between w-full min-w-100 py-4 text-14 bg-transparent cursor-pointer text-black outline-none transition-all ${
          open ? 'border-b-2 border-primary' : 'border-b border-neutral-40 hover:border-black'
        }`}
      >
        <div className='overflow-hidden text-ellipsis whitespace-nowrap text-left flex-1'>
          {selectedLabel || <span className='text-neutral-50'>{placeholder}</span>}
        </div>
        <ArrowDropDownIcon
          sx={{
            color: '#606060',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            sx={{
              width: anchorRef.current ? Math.max(anchorRef.current.offsetWidth, 240) : 240,
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Search */}
            <div className='p-12 border-b border-neutral-20'>
              <div className='flex items-center px-12 py-6 bg-neutral-5 border border-neutral-20 rounded text-neutral-50'>
                <SearchIcon sx={{ color: 'inherit', fontSize: 20 }} />
                <InputBase
                  placeholder='Search'
                  value={search}
                  onChange={handleSearchChange}
                  autoFocus
                  className='flex-1 ml-4 text-14'
                  sx={{
                    '& .MuiInputBase-input': {
                      p: 0,
                      '&::placeholder': {
                        color: '#9f9f9f',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Options list */}
            {(() => {
              let optionsContent;
              if (isLoading) {
                optionsContent = (
                  <div className='flex items-center justify-center py-16'>
                    <CircularProgress size={20} />
                  </div>
                );
              } else if (filteredOptions.length > 0) {
                optionsContent = (
                  <>
                    {filteredOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type='button'
                        onClick={() => handleSelect(opt.value)}
                        className={`w-full text-left px-16 py-12 text-12 cursor-pointer transition-colors hover:bg-neutral-5 ${
                          internalValue === opt.value ? 'bg-green-10 font-medium' : ''
                        }`}
                        style={{ display: 'block' }}
                      >
                        {opt.label}
                      </button>
                    ))}
                    {/* Sentinel for infinite scroll */}
                    {hasNextPage && (
                      <div ref={sentinelRef} className='flex items-center justify-center py-8'>
                        {isFetchingNextPage && <CircularProgress size={16} />}
                      </div>
                    )}
                  </>
                );
              } else {
                optionsContent = <div className='px-16 py-12 text-14 text-neutral-40'>No results found</div>;
              }
              return (
                <div ref={listRef} className='max-h-[220px] overflow-y-auto scrollbar-default'>
                  {optionsContent}
                </div>
              );
            })()}

            {/* Add New button */}
            <div className='p-12 border-t border-neutral-20 bg-neutral-5'>
              <button
                type='button'
                onClick={() => {
                  onAddNew?.();
                  setOpen(false);
                }}
                className='w-full py-10 rounded text-white text-14 font-semibold cursor-pointer transition-colors bg-primary hover:bg-green-60'
              >
                {addNewLabel}
              </button>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};
